import { UserRoles, TipsterStatus, UserStatus } from '@app/graphql'
import { USER_CACHE_KEY } from '@app/constants'


const CACHE_VERSION = '1'
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

export interface CachedUserData {
  id: string
  firstName: string | null
  lastName: string | null
  displayName: string
  emailAddress: string
  phoneNumber: string | null
  dateOfBirth: string | null
  countryId: string | null
  role: UserRoles
  userStatus: UserStatus
  tipsterStatus: TipsterStatus | null
  permissions: string[]
  avatarUrl: string | null
  background: string | null
  gender: string | null
  bio: string | null
  unitShare: number | null
  unitValue: number | null
  oddsFormat: string | null
  onboardingStage: number
  onboardingProgress: any
  channelsWhereUserIsTipster: Array<{
    id: string
    channelName: string
    logo: string | null
  }> | null
  followedChannels: any[] | null
  hasPendingSubscription: boolean
  cachedAt: number
  version: string
  isTipsterAdmin: boolean
  favoriteTenantBookieId: string | null
}

const setCurrentChannelId = (userData) => {
  const channelId =
    userData?.role === UserRoles.Tipster
      ? userData?.channelsWhereUserIsTipster?.[0]?.id
      : userData?.followedChannels?.[0]?.id

  if (!channelId) return

  localStorage.setItem('currentChannelId', channelId)
}

export const UserCacheHelper = {
  save(userData: any): void {
    try {
      if (!userData?.userCurrent) return

      const user = userData.userCurrent
      const cached: CachedUserData = {
        id: user.id,
        firstName: user.firstName,
        isTipsterAdmin: user.isTipsterAdmin,
        lastName: user.lastName,
        displayName: user.displayName,
        emailAddress: user.emailAddress,
        phoneNumber: user.phoneNumber,
        dateOfBirth: user.dateOfBirth,
        countryId: user.countryId,
        role: user.role,
        userStatus: user.userStatus,
        tipsterStatus: user.tipsterStatus,
        permissions: user.permissions || [],
        avatarUrl: user.avatarUrl,
        background: user.background,
        gender: user.gender,
        bio: user.bio,
        unitShare: user.unitShare,
        unitValue: user.unitValue,
        oddsFormat: user.oddsFormat ?? null,
        onboardingStage: user.onboardingStage,
        onboardingProgress: user.onboardingProgress,
        channelsWhereUserIsTipster: user.channelsWhereUserIsTipster,
        followedChannels: user.followedChannels,
        favoriteTenantBookieId: user.favoriteTenantBookieId ?? null,
        hasPendingSubscription: userData.hasPendingSubscription ?? false,
        cachedAt: Date.now(),
        version: CACHE_VERSION,
      }
      setCurrentChannelId(cached)
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(cached))
    } catch (error) {
      console.error('Failed to cache user data:', error)
    }
  },

  load(): CachedUserData | null {
    try {
      const cached = localStorage.getItem(USER_CACHE_KEY)
      if (!cached) return null

      const data: CachedUserData = JSON.parse(cached)

      if (data.version !== CACHE_VERSION) {
        UserCacheHelper.clear()
        return null
      }

      const age = Date.now() - data.cachedAt
      if (age > CACHE_EXPIRY_MS) {
        UserCacheHelper.clear()
        return null
      }

      return data
    } catch (error) {
      console.error('Failed to load cached user data:', error)
      UserCacheHelper.clear()
      return null
    }
  },

  updateFavoriteTenantBookieId(favoriteTenantBookieId: string | null): void {
    try {
      const cached = UserCacheHelper.load()
      if (!cached) return
      cached.favoriteTenantBookieId = favoriteTenantBookieId
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(cached))
    } catch (error) {
      console.error('Failed to update cached favorite bookie:', error)
    }
  },

  clear(): void {
    try {
      localStorage.removeItem(USER_CACHE_KEY)
    } catch (error) {
      console.error('Failed to clear user cache:', error)
    }
  },

  isValid(): boolean {
    const cached = UserCacheHelper.load()
    return cached !== null
  },
}
